// Hub ▸ Marketing ▸ Spend & Budgets (slice S2, CEO 2026-10-21):
//   1. this month's pacing per plan line — spent, expected by now, month on
//      this pace, over / under in words, and the line's gross profit + POAS (revenue + ROAS for marketing-only viewers) so
//      a manager doesn't cut a winner;
//   2. the plan editor — lines × months, one-click "use the approved plan"
//      (fills the draft, saves nothing), CSV export / import; every save is
//      audited (history below); unsaved changes are never lost silently;
//   3. the last six months: plan, spent, variance, gross profit, POAS.
// Writes: admin + marketing. Finance reads.
import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Download, Upload } from "lucide-react";
import { useAuth } from "@/auth/AuthProvider";
import { api, apiErrorMessage } from "@/lib/api";
import { cn } from "@/lib/utils";
import { Bullet, Legend, SERIES } from "./charts";
import {
  EmptyState, ErrorLine, FreshnessStamp, InfoTip, Num, Panel, Pill, Skeleton, downloadText, money, monthLabel, useApi, type Tone,
} from "./kit";
import { RATIO_BARS, WORDS, ratioOf, valueOf } from "./measure";
import type { BudgetsResponse, BudgetsReady, Measure, PaceFlag, Pacing, PacingReady, PacingResponse } from "./types";

const addMonth = (m: string, n: number) => { const d = new Date(Date.UTC(Number(m.slice(0, 4)), Number(m.slice(5, 7)) - 1 + n, 1)); return d.toISOString().slice(0, 7); };
const thisMonth = () => new Date().toISOString().slice(0, 7);
const toneFor = (f: PaceFlag): Tone => (f === "over" || f === "unbudgeted" ? "risk" : f === "under" ? "warn" : f === "on_pace" ? "ok" : "neutral");
const isOver = (p: Pacing) => p.flag === "over" || p.flag === "unbudgeted";
const pctWord = (p: Pacing) => (p.variancePct !== null && (p.flag === "over" || p.flag === "under") ? ` ${p.variancePct > 0 ? "+" : ""}${Math.round(p.variancePct * 100)}%` : "");

const PACE_FORMULAS = {
  expected: "Expected by now = month budget × days counted ÷ days in the month. Days counted run through yesterday — the same day the Overview counts through.",
  forecast: "Month on this pace = spent so far ÷ days counted × days in the month.",
  flag: "On pace = within 10% of expected by now. Over / under = more than 10% either way. Spending with no budget = spend on a line whose budget is $0. The daily alert e-mail fires above 15% over (above 50% in the first days of a month).",
  line: "Which line a dollar counts against: Google's own campaign type decides Shopping (Shopping and Performance Max) vs Search; only when Google doesn't say, a campaign named Shopping / PMax counts as Shopping. Spend uploaded as sample packs counts against Sample packs. TikTok and Reddit have no line and show as not in the plan.",
  gp: "Gross profit of paid orders credited to the line's ads this month (the last ad clicked) — the same basis as the Overview's POAS — and gross profit ÷ the line's spend. Sample packs can't be credited to a click, so they show no gross profit. Low early in a month: business clients often order weeks after the click.",
  revenue: "Revenue of paid orders credited to the line's ads this month (the last ad clicked) — the same basis as the Overview's ROAS — and revenue ÷ the line's spend. Sample packs can't be credited to a click, so they show none. Low early in a month: business clients often order weeks after the click.",
  historyGp: "Gross profit of that month's paid orders credited to the last ad clicked (paid ad channels). POAS = this ÷ the month's spend, sample packs included — the same numbers the Overview divides.",
  historyRevenue: "Revenue of that month's paid orders credited to the last ad clicked (paid ad channels). ROAS = this ÷ the month's spend, sample packs included — the same numbers the Overview divides.",
};

/** POAS (admin + finance) or ROAS (marketing-only viewers, CEO 2026-10-22), coloured by that measure's bars. */
function RatioText({ v, spend, value, m }: { v: number | null; spend: number; value: number | null; m: Measure }) {
  if (value === null) return <span className="text-ink-muted">—</span>;
  if (spend > 0 && value === 0) return <span className="font-semibold text-risk">$0 back</span>;
  if (v === null) return <span className="text-ink-muted">—</span>;
  const b = RATIO_BARS[m];
  return <span className={cn(v < b.low ? "font-semibold text-risk" : v >= b.high ? "font-semibold text-ok" : "")}>{v.toFixed(2)}×</span>;
}

// ─── pacing ─────────────────────────────────────────────────────────────────

function PacingPanel({ d }: { d: PacingReady }) {
  const t = d.total; const m = d.measure ?? "gp"; const W = WORDS[m];
  return (
    <Panel title={`${monthLabel(d.month)} pacing`} info={<InfoTip label="pacing" formula={`${PACE_FORMULAS.expected} ${PACE_FORMULAS.forecast} ${PACE_FORMULAS.flag}`} />}
      actions={<Legend items={[{ label: "Spent", color: SERIES.spend }, { label: "Over pace", color: "#993A32", shape: "hatch" }, { label: "Expected by now", color: "#080808", shape: "tick" }, { label: "Month budget", color: "#E6E6E1" }]} />}>
      <div className="flex flex-col gap-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-label text-ink-muted">Spend counted through <span className="whitespace-nowrap font-semibold text-ink">{monthLabel(d.throughDay.slice(0, 7), false)} {Number(d.throughDay.slice(8, 10))}</span> · day {t.daysElapsed} of {t.daysInMonth}</p>
          <Pill tone={toneFor(t.flag)}>{d.flagLabels[t.flag]}{pctWord(t)}</Pill>
        </div>
        <dl className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-4">
          {[["Month budget", money(t.budgetCents)], ["Spent so far", money(t.spentCents)], ["Expected by now", money(t.expectedCents)], ["Month on this pace", money(t.forecastCents)]].map(([k, v]) => (
            <div key={k}><dt className="text-label text-ink-muted">{k}</dt><dd className="text-[28px] font-semibold leading-tight tabular-nums text-ink">{v}</dd></div>
          ))}
        </dl>
        {d.notes.map((n) => <p key={n} className="text-label text-warn">{n}</p>)}
        <div className="relative -mx-4 overflow-x-auto sm:-mx-6">
          <table className="w-full min-w-[900px] border-collapse text-label">
            <caption className="sr-only">Pacing by plan line</caption>
            <thead>
              <tr className="border-b border-rule text-meta uppercase tracking-[0.06em] text-ink-muted">
                <th scope="col" className="sticky left-0 z-[1] w-[220px] min-w-[220px] bg-white py-2.5 pl-4 pr-3 text-left font-semibold sm:pl-6"><span className="inline-flex items-center gap-0.5">Line <InfoTip label="plan lines" formula={PACE_FORMULAS.line} /></span></th>
                <th scope="col" className="w-[24%] px-3 text-left font-semibold">Spent vs expected</th>
                <th scope="col" className="px-3 text-right font-semibold">Budget</th>
                <th scope="col" className="px-3 text-right font-semibold">Spent</th>
                <th scope="col" className="px-3 text-right font-semibold">Expected</th>
                <th scope="col" className="px-3 text-right font-semibold">On this pace</th>
                <th scope="col" className="px-3 text-right font-semibold"><span className="inline-flex items-center justify-end gap-0.5">{W.value} (last ad clicked) <InfoTip label={`${W.valueLower} by line`} formula={m === "revenue" ? PACE_FORMULAS.revenue : PACE_FORMULAS.gp} align="right" /></span></th>
                <th scope="col" className="py-2.5 pl-3 pr-4 text-right font-semibold sm:pr-6">{W.ratio} (last ad clicked)</th>
              </tr>
            </thead>
            <tbody>
              {d.lines.map((l) => { const { line, label, pacing: p } = l; const value = valueOf(l, m); return (
                <tr key={line} className="border-b border-rule/70 bg-white last:border-0">
                  <th scope="row" className="sticky left-0 z-[1] bg-white py-3 pl-4 pr-3 text-left font-semibold sm:pl-6">
                    <span className="block">{label}</span>
                    <span className="mt-1 block"><Pill tone={toneFor(p.flag)}>{d.flagLabels[p.flag]}{pctWord(p)}</Pill></span>
                  </th>
                  <td className="px-3"><Bullet value={p.spentCents} target={p.expectedCents} track={p.budgetCents} over={isOver(p)} label={`${label}: ${d.flagLabels[p.flag]}${pctWord(p)}. ${money(p.spentCents)} spent, ${money(p.expectedCents)} expected by now, ${money(p.budgetCents)} budget.`} /></td>
                  <td className="px-3 text-right tabular-nums"><Num v={p.budgetCents}>{money(p.budgetCents)}</Num></td>
                  <td className="px-3 text-right tabular-nums"><Num v={p.spentCents}>{money(p.spentCents)}</Num></td>
                  <td className="px-3 text-right tabular-nums"><Num v={p.expectedCents}>{money(p.expectedCents)}</Num></td>
                  <td className="px-3 text-right tabular-nums"><Num v={p.forecastCents}>{money(p.forecastCents)}</Num></td>
                  <td className="px-3 text-right tabular-nums"><Num v={value}>{money(value)}</Num></td>
                  <td className="py-3 pl-3 pr-4 text-right tabular-nums sm:pr-6"><RatioText v={ratioOf(l, m)} spend={p.spentCents} value={value} m={m} /></td>
                </tr>
              ); })}
            </tbody>
          </table>
        </div>
        <p className="text-meta text-ink-muted">
          Daily alert e-mail to admins and the marketing team when a line runs more than {Math.round(d.thresholds.alert * 100)}% over pace: <span className="font-semibold text-ink">{d.alertsOn ? "on" : "off — an admin can switch it on"}</span>.
        </p>
      </div>
    </Panel>
  );
}

function HistoryPanel({ d }: { d: PacingReady }) {
  if (!d.history.length) return null;
  const m = d.measure ?? "gp"; const W = WORDS[m];
  const any = d.history.some((h) => h.budgetCents || h.spentCents);
  return (
    <Panel title="Last 6 months — plan vs actual">
      {!any ? <EmptyState title="No plan or spend in the last six months">Months fill in as budgets are saved below and ad spend arrives.</EmptyState> : (
        <div className="relative -mx-4 overflow-x-auto sm:-mx-6">
          <table className="w-full min-w-[640px] border-collapse text-label">
            <caption className="sr-only">Plan vs actual by month</caption>
            <thead><tr className="border-b border-rule text-meta uppercase tracking-[0.06em] text-ink-muted">
              <th scope="col" className="py-2.5 pl-4 pr-3 text-left font-semibold sm:pl-6">Month</th>
              <th scope="col" className="px-3 text-right font-semibold">Plan</th>
              <th scope="col" className="px-3 text-right font-semibold">Spent</th>
              <th scope="col" className="px-3 text-right font-semibold">Variance</th>
              <th scope="col" className="px-3 text-right font-semibold"><span className="inline-flex items-center justify-end gap-0.5">{W.value} credited to ads <InfoTip label={`monthly ${W.valueLower}`} formula={m === "revenue" ? PACE_FORMULAS.historyRevenue : PACE_FORMULAS.historyGp} align="right" /></span></th>
              <th scope="col" className="py-2.5 pl-3 pr-4 text-right font-semibold sm:pr-6">{W.ratio} (last ad clicked)</th>
            </tr></thead>
            <tbody>
              {d.history.map((h) => {
                const over = h.budgetCents > 0 && h.spentCents > h.budgetCents * 1.1;
                return (
                  <tr key={h.month} className="border-b border-rule/70 last:border-0">
                    <th scope="row" className="whitespace-nowrap py-3 pl-4 pr-3 text-left font-semibold sm:pl-6">{monthLabel(h.month)}</th>
                    <td className="px-3 text-right tabular-nums">{h.budgetCents ? money(h.budgetCents) : <span className="text-ink-muted">no plan</span>}</td>
                    <td className="px-3 text-right tabular-nums"><Num v={h.spentCents}>{money(h.spentCents)}</Num></td>
                    <td className={cn("px-3 text-right tabular-nums", over ? "font-semibold text-risk" : "text-ink-muted")}>{!h.budgetCents ? "—" : h.varianceCents === 0 ? "on plan" : `${money(Math.abs(h.varianceCents))} ${h.varianceCents > 0 ? "over" : "under"}`}</td>
                    <td className="px-3 text-right tabular-nums"><Num v={valueOf(h, m)}>{money(valueOf(h, m))}</Num></td>
                    <td className="py-3 pl-3 pr-4 text-right tabular-nums sm:pr-6"><RatioText v={ratioOf(h, m)} spend={h.spentCents} value={valueOf(h, m)} m={m} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  );
}

// ─── plan editor ────────────────────────────────────────────────────────────

const key = (m: string, l: string) => `${m}|${l}`;
const dollars = (cents: number | undefined) => (cents === undefined ? "" : (cents / 100).toLocaleString("en-US", { minimumFractionDigits: cents % 100 === 0 ? 0 : 2, maximumFractionDigits: 2 }));
const parseDollars = (s: string): number | null | "bad" => {
  const v = s.replace(/[$,\s]/g, "");
  if (v === "") return null;
  if (!/^\d+(\.\d{1,2})?$/.test(v)) return "bad";
  return Math.round(Number(v) * 100);
};
const LOSE = "You have unsaved budget changes. Leave without saving them?";

function PlanEditor() {
  const [start, setStart] = useState(thisMonth());
  const end = addMonth(start, 5);
  const { data, error, loading, reload } = useApi<BudgetsResponse>(`/api/marketing/budgets?from=${start}&to=${end}`);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [msg, setMsg] = useState<{ tone: "ok" | "risk"; text: string; list?: string[] } | null>(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState<{ csv: string; cells: number; months: number } | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const d: BudgetsReady | null = data && data.ready ? data : null;
  const saved = useMemo(() => new Map((d?.cells ?? []).map((c) => [key(c.month, c.line), c.amountCents])), [d]);
  useEffect(() => { setDraft({}); }, [d]);
  const dirty = Object.entries(draft).filter(([k, v]) => { const p = parseDollars(v); return p === "bad" || (p ?? undefined) !== saved.get(k); });
  // Never lose a draft silently: closing / reloading the tab asks first.
  useEffect(() => {
    if (!dirty.length) return;
    const warn = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = LOSE; return LOSE; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty.length]);
  const guard = (fn: () => void) => { if (!dirty.length || window.confirm(LOSE)) fn(); };

  if (error) return <ErrorLine onRetry={reload}>{error}</ErrorLine>;
  if (loading && !data) return <Skeleton rows={1} label="Loading the plan" />;
  if (data && !data.ready) return <EmptyState title="Budgets aren't switched on yet">{data.detail}</EmptyState>;
  if (!d) return null;

  const value = (m: string, l: string) => (key(m, l) in draft ? draft[key(m, l)]! : dollars(saved.get(key(m, l))));
  const cellCents = (m: string, l: string): number => { const p = parseDollars(value(m, l)); return typeof p === "number" ? p : 0; };
  const bad = Object.entries(draft).filter(([, v]) => parseDollars(v) === "bad").map(([k]) => k);
  const changed = dirty.filter(([k]) => !bad.includes(k));
  const current = thisMonth();

  function fillApproved() {
    const next = { ...draft };
    for (const m of d!.months) if (m >= current) for (const l of d!.lines) next[key(m, l.line)] = dollars(l.approvedCents);
    setDraft(next);
    setMsg({ tone: "ok", text: `Filled ${d!.months.filter((m) => m >= current).length} month(s) with the approved $${(d!.approvedTotalCents / 100).toLocaleString("en-US")} plan. Nothing is saved until you press Save plan.` });
  }
  async function save() {
    setBusy(true); setMsg(null);
    try {
      const cells = changed.map(([k, v]) => { const [month, line] = k.split("|"); const p = parseDollars(v); return { month: month!, line: line!, amountCents: p === "bad" ? null : p }; });
      const r = await api<{ saved: number }>("/api/marketing/budgets", { method: "PUT", body: JSON.stringify({ cells }) });
      setMsg({ tone: "ok", text: `Saved ${r.saved} change${r.saved === 1 ? "" : "s"}. Each one is in the change history below.` });
      reload();
    } catch (e) { setMsg({ tone: "risk", text: apiErrorMessage(e, "Couldn't save the plan.") }); }
    finally { setBusy(false); }
  }
  async function exportCsv() {
    try { downloadText(`marketing-plan-${start}-to-${end}.csv`, await api<string>(`/api/marketing/budgets/export?from=${start}&to=${end}`, { headers: { Accept: "text/csv" } })); }
    catch (e) { setMsg({ tone: "risk", text: apiErrorMessage(e, "Couldn't export.") }); }
  }
  async function onFile(f: File | undefined) {
    if (!f) return;
    setMsg(null); setPreview(null);
    const csv = await f.text();
    try {
      const r = await api<{ cells: Array<{ month: string }> }>("/api/marketing/budgets/import", { method: "POST", body: JSON.stringify({ csv, apply: false }) });
      setPreview({ csv, cells: r.cells.length, months: new Set(r.cells.map((c) => c.month)).size });
    } catch (e) {
      const body = (e as { body?: { errors?: string[] } }).body;
      setMsg({ tone: "risk", text: apiErrorMessage(e, "That file couldn't be read."), list: body?.errors });
    } finally { if (fileRef.current) fileRef.current.value = ""; }
  }
  async function applyImport() {
    if (!preview) return;
    setBusy(true);
    try {
      const r = await api<{ saved: number }>("/api/marketing/budgets/import", { method: "POST", body: JSON.stringify({ csv: preview.csv, apply: true }) });
      setMsg({ tone: "ok", text: `Imported — ${r.saved} change${r.saved === 1 ? "" : "s"} saved.` }); setPreview(null); reload();
    } catch (e) { setMsg({ tone: "risk", text: apiErrorMessage(e, "Couldn't import.") }); }
    finally { setBusy(false); }
  }

  const monthTotal = (m: string) => d.lines.reduce((a, l) => a + cellCents(m, l.line), 0);
  const lineTotal = (l: string) => d.months.reduce((a, m) => a + cellCents(m, l), 0);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <button type="button" aria-label="Earlier months" onClick={() => guard(() => setStart(addMonth(start, -3)))} className="rounded-md border border-rule p-2 hover:bg-paper"><ChevronLeft className="h-4 w-4" /></button>
          <span className="min-w-[180px] whitespace-nowrap text-center text-label font-semibold">{monthLabel(start)} – {monthLabel(end)}</span>
          <button type="button" aria-label="Later months" onClick={() => guard(() => setStart(addMonth(start, 3)))} className="rounded-md border border-rule p-2 hover:bg-paper"><ChevronRight className="h-4 w-4" /></button>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {d.canEdit && <button type="button" onClick={fillApproved} className="h-10 rounded-md border border-ink px-4 text-label font-semibold hover:bg-paper">Use the approved plan (${(d.approvedTotalCents / 100).toLocaleString("en-US")}/mo)</button>}
          <button type="button" onClick={exportCsv} className="inline-flex h-10 items-center gap-1.5 rounded-md border border-rule px-3 text-label hover:bg-paper"><Download className="h-4 w-4" aria-hidden />Export CSV</button>
          {d.canEdit && (
            <label className="inline-flex h-10 cursor-pointer items-center gap-1.5 rounded-md border border-rule px-3 text-label hover:bg-paper focus-within:outline focus-within:outline-2 focus-within:outline-focus">
              <Upload className="h-4 w-4" aria-hidden />Import CSV
              <input ref={fileRef} type="file" accept=".csv,text/csv" className="sr-only" onChange={(e) => void onFile(e.target.files?.[0])} />
            </label>
          )}
        </div>
      </div>
      {!d.canEdit && <p className="rounded-md bg-paper px-4 py-2 text-label text-ink-muted">You're viewing the plan read-only. Admins and the marketing team change it.</p>}
      {msg && (
        <div role="status" className={cn("rounded-md px-4 py-3 text-label", msg.tone === "ok" ? "bg-ok-bg text-ok" : "bg-risk-bg text-risk")}>
          {msg.text}
          {msg.list && <ul className="mt-1 list-disc pl-5">{msg.list.map((x) => <li key={x}>{x}</li>)}</ul>}
        </div>
      )}
      {preview && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-ink bg-paper px-4 py-3 text-label">
          <span>The file is valid: {preview.cells} amount{preview.cells === 1 ? "" : "s"} across {preview.months} month{preview.months === 1 ? "" : "s"}. Blank cells are left as they are.</span>
          <span className="flex gap-2">
            <button type="button" onClick={() => setPreview(null)} className="h-9 rounded-md px-3 hover:bg-white">Cancel</button>
            <button type="button" disabled={busy} onClick={applyImport} className="h-9 rounded-md bg-ink px-4 font-semibold text-white disabled:opacity-50">Save these amounts</button>
          </span>
        </div>
      )}
      <div className="relative -mx-4 overflow-x-auto sm:-mx-6">
        <table className="w-full min-w-[900px] border-collapse text-label">
          <caption className="sr-only">Marketing plan by line and month, in dollars</caption>
          <thead>
            <tr className="border-b border-rule">
              <th scope="col" className="sticky left-0 z-[1] w-[200px] min-w-[200px] bg-white py-2.5 pl-4 pr-3 text-left text-meta font-semibold uppercase tracking-[0.06em] text-ink-muted sm:pl-6">Line</th>
              {d.months.map((m) => <th key={m} scope="col" className={cn("whitespace-nowrap px-2 py-2.5 text-right text-meta font-semibold uppercase tracking-[0.06em]", m === current ? "text-ink" : "text-ink-muted")}>{monthLabel(m)}{m === current && <span className="block normal-case tracking-normal">this month</span>}</th>)}
              <th scope="col" className="py-2.5 pl-2 pr-4 text-right text-meta font-semibold uppercase tracking-[0.06em] text-ink-muted sm:pr-6">6 months</th>
            </tr>
          </thead>
          <tbody>
            {d.lines.map((l) => (
              <tr key={l.line} className="border-b border-rule/70 bg-white">
                <th scope="row" className="sticky left-0 z-[1] bg-white py-2 pl-4 pr-3 text-left font-semibold sm:pl-6">{l.label}<span className="block text-meta font-normal text-ink-muted">approved ${(l.approvedCents / 100).toLocaleString("en-US")}/mo</span></th>
                {d.months.map((m) => {
                  const k = key(m, l.line);
                  const isChanged = changed.some(([dk]) => dk === k);
                  return (
                    <td key={m} className="px-2 py-2 text-right">
                      <label className="sr-only" htmlFor={`b-${k}`}>{l.label} {monthLabel(m)}</label>
                      <div className={cn("ml-auto flex h-10 w-[112px] items-center rounded-md border bg-white pl-2 focus-within:outline focus-within:outline-2 focus-within:outline-focus",
                        bad.includes(k) ? "border-risk" : isChanged ? "border-ink bg-paper" : "border-rule", !d.canEdit && "border-transparent bg-transparent")}>
                        <span className="text-ink-muted" aria-hidden>$</span>
                        <input id={`b-${k}`} inputMode="decimal" readOnly={!d.canEdit} value={value(m, l.line)} placeholder="—" aria-invalid={bad.includes(k) || undefined}
                          onChange={(e) => setDraft({ ...draft, [k]: e.target.value })}
                          className="h-full w-full min-w-0 bg-transparent pr-2 text-right tabular-nums outline-none placeholder:text-ink-muted/60" />
                      </div>
                    </td>
                  );
                })}
                <td className="py-2 pl-2 pr-4 text-right font-semibold tabular-nums sm:pr-6">{money(lineTotal(l.line))}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <th scope="row" className="sticky left-0 z-[1] bg-white py-3 pl-4 pr-3 text-left font-semibold sm:pl-6">Total</th>
              {d.months.map((m) => <td key={m} className="px-2 py-3 text-right font-semibold tabular-nums">{money(monthTotal(m))}</td>)}
              <td className="py-3 pl-2 pr-4 text-right font-semibold tabular-nums sm:pr-6">{money(d.months.reduce((a, m) => a + monthTotal(m), 0))}</td>
            </tr>
          </tfoot>
        </table>
      </div>
      {d.canEdit && (
        <div className="flex flex-wrap items-center justify-end gap-3">
          {bad.length > 0 && <span className="text-label text-risk">Fix the amounts outlined in red (dollars and cents only).</span>}
          <span className="text-label text-ink-muted">{changed.length ? `${changed.length} unsaved change${changed.length === 1 ? "" : "s"}` : "No unsaved changes"}</span>
          <button type="button" disabled={!dirty.length || busy} onClick={() => guard(() => setDraft({}))} className="h-11 rounded-md px-4 text-label hover:bg-paper disabled:opacity-40">Discard changes</button>
          <button type="button" disabled={!changed.length || bad.length > 0 || busy} onClick={save} className="h-11 rounded-md bg-ink px-6 text-label font-semibold text-white disabled:opacity-40">Save plan</button>
        </div>
      )}
      {d.history.length > 0 && (
        <details className="rounded-md border border-rule">
          <summary className="cursor-pointer px-4 py-3 text-label font-semibold">Change history ({d.history.length} most recent)</summary>
          <ul className="divide-y divide-rule/70 border-t border-rule">
            {d.history.map((h, i) => (
              <li key={i} className="flex flex-wrap justify-between gap-2 px-4 py-2.5 text-label">
                <span>{d.lines.find((l) => l.line === h.line)?.label ?? h.line} · {monthLabel(h.month)}: {h.fromCents === null ? "not set" : money(h.fromCents)} → <span className="font-semibold">{h.toCents === null ? "cleared" : money(h.toCents)}</span></span>
                <span className="text-ink-muted">{h.by ?? "Someone"} · {new Date(h.at).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" })}</span>
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

export function BudgetsTab() {
  const { user } = useAuth();
  const isAdmin = !!user?.roles?.includes("admin");
  const pacing = useApi<PacingResponse>("/api/marketing/pacing");
  const p = pacing.data && pacing.data.ready ? pacing.data : null;
  return (
    <div className="space-y-6">
      {p && <div className="flex justify-end"><FreshnessStamp freshness={p.freshness} isAdmin={isAdmin} onRefreshed={pacing.reload} throughDay={p.throughDay} /></div>}
      {pacing.error && <ErrorLine onRetry={pacing.reload}>{pacing.error}</ErrorLine>}
      {pacing.loading && !pacing.data && <Skeleton rows={1} label="Loading pacing" />}
      {pacing.data && !pacing.data.ready && <EmptyState title="Pacing isn't switched on yet">{pacing.data.detail}</EmptyState>}
      {p && <PacingPanel d={p} />}
      <Panel title="Budget plan by month" info={<InfoTip label="the plan" formula={`One amount per line per month, in dollars. Blank = no budget. ${PACE_FORMULAS.line} Every saved change is recorded with who made it and when.`} />}>
        <PlanEditor />
      </Panel>
      {p && <HistoryPanel d={p} />}
    </div>
  );
}
